import React, { useMemo, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useFeedback } from '../context/FeedbackContext';
import { useNavigate } from 'react-router-dom';
import { useMyFeedback } from '../hooks/useFeedbackInstanceApi';
import { useDeleteFeedback } from '../hooks/useFeedbackApi';
import toast from 'react-hot-toast';
import {
  FileText,
  CheckCircle,
  Clock,
  TrendingUp,
  Calendar,
  ArrowRight,
  User,
  Users,
  Trash2,
  AlertTriangle,
} from 'lucide-react';

interface DashboardProps {
  view: 'dashboard' | 'feedback' | 'assigned';
}

const Dashboard: React.FC<DashboardProps> = ({ view = 'dashboard' }) => {
  const { currentUser } = useAuth();
  const { feedbackList, instances } = useFeedback();
  const { data: myFeedbackData } = useMyFeedback();
  const navigate = useNavigate();
  const deleteFeedback = useDeleteFeedback();
  const [deleteConfirm, setDeleteConfirm] = useState<{ id: string; title: string } | null>(null);

  const handleDeleteFeedback = async () => {
    if (!deleteConfirm) return;
    try {
      await deleteFeedback.mutateAsync(deleteConfirm.id);
      toast.success('Feedback deleted');
    } catch {
      toast.error('Failed to delete feedback');
    }
    setDeleteConfirm(null);
  };

  const userFeedback = useMemo(() => {
    if (currentUser?.role === 'admin') return feedbackList;
    if (currentUser?.role === 'creator') {
      return feedbackList.filter((s) => String(s.createdBy) === String(currentUser.id));
    }
    return [];
  }, [feedbackList, currentUser]);

  const assignedInstances = useMemo(() => {
    if (!currentUser) return [];
    if (myFeedbackData && Array.isArray(myFeedbackData)) {
      return myFeedbackData
        .map(item => ({
          id: item.id,
          feedbackInstanceId: item.feedbackInstanceId,
          feedbackId: item.feedbackId,
          assignedTo: Array.isArray(item.assignedTo) ? item.assignedTo : [],
          assigneeType: item.assigneeType,
          frequency: item.frequency,
          startsAt: new Date(item.startsAt),
          endsAt: item.endsAt ? new Date(item.endsAt) : undefined,
          status: item.status,
          hasUserResponded: item.hasUserResponded ?? false,
        }))
        .sort((a, b) => b.startsAt.getTime() - a.startsAt.getTime());
    }
    return instances
      .filter(
        (instance) =>
          instance.status === 'active' &&
          (instance.assignedTo.includes(currentUser.id) || instance.assigneeType === 'role')
      )
      .map(instance => ({
        id: instance.id,
        feedbackInstanceId: instance.id,
        feedbackId: instance.feedbackId,
        assignedTo: instance.assignedTo,
        assigneeType: instance.assigneeType,
        frequency: instance.frequency,
        startsAt: new Date(instance.startDate),
        endsAt: instance.endDate ? new Date(instance.endDate) : undefined,
        status: instance.status,
        hasUserResponded: instance.hasUserResponded ?? false,
      }));
  }, [instances, currentUser, myFeedbackData]);


  const stats = useMemo(() => {
    const baseStats = [];

    if (currentUser?.role === 'admin' || currentUser?.role === 'creator') {
      baseStats.push({
        title: currentUser.role === 'admin' ? 'All Feedback' : 'My Feedback',
        value: userFeedback.length,
        icon: FileText,
        color: 'bg-blue-500',
        description: currentUser.role === 'admin' ? 'Total feedback in system' : 'Total feedback created',
      });
    }

    const pendingInstances = assignedInstances.filter(i => !i.hasUserResponded);
    const completedInstances = assignedInstances.filter(i => i.hasUserResponded);

    baseStats.push(
      {
        title: 'Assigned Feedback',
        value: pendingInstances.length,
        icon: CheckCircle,
        color: 'bg-green-500',
        description: 'Feedback to complete',
      },
      {
        title: 'Completed',
        value: completedInstances.length,
        icon: Clock,
        color: 'bg-purple-500',
        description: 'Feedback completed',
      },
      {
        title: 'Response Rate',
        value: assignedInstances.length > 0
          ? `${Math.round((completedInstances.length / assignedInstances.length) * 100)}%`
          : '0%',
        icon: TrendingUp,
        color: 'bg-orange-500',
        description: 'Your completion rate',
      }
    );

    return baseStats;
  }, [userFeedback.length, assignedInstances, currentUser?.role]);

  if (view === 'feedback') {
    return (
      <div className="p-6 space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">My Feedback</h1>
          <button
            onClick={() => navigate('/feedback/create')}
            className="px-4 py-2 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors"
          >
            Create Feedback
          </button>
        </div>

        <div className="grid gap-4">
          {userFeedback.map((feedback) => (
            <div
              key={feedback.id}
              className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => navigate(`/feedback/edit/${feedback.id}`)}
            >
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      {feedback.title}
                    </h3>
                    <div className="flex items-center gap-2 ml-3">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                        feedback.isPublished && feedback.publishedVersions && feedback.publishedVersions.length > 0
                          ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                          : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                      }`}>
                        {feedback.isPublished && feedback.publishedVersions && feedback.publishedVersions.length > 0
                          ? `Published: v${feedback.publishedVersions.join(', v')}`
                          : 'Draft'}
                      </span>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeleteConfirm({ id: feedback.id, title: feedback.title });
                        }}
                        className="p-1.5 text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md transition-colors"
                        title="Delete feedback"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                  <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                    {feedback.description}
                  </p>
                  <div className="mt-4 flex items-center space-x-4 text-sm text-gray-500 dark:text-gray-400">
                    <span className="flex items-center">
                      <FileText className="h-4 w-4 mr-1" />
                      {feedback.questionCount} questions
                    </span>
                    <span className="flex items-center">
                      <Calendar className="h-4 w-4 mr-1" />
                      {new Date(feedback.createdAt).toLocaleDateString()}
                    </span>
                  </div>
                </div>
                <ArrowRight className="h-5 w-5 text-gray-400 ml-4" />
              </div>
            </div>
          ))}
        </div>

        {deleteConfirm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-red-100 dark:bg-red-900/30 rounded-full">
                  <AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-400" />
                </div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Delete Feedback
                </h2>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
                Are you sure you want to delete <span className="font-medium text-gray-900 dark:text-white">{deleteConfirm.title}</span>? This will permanently remove the feedback, all its versions, questions, and any associated responses. This action cannot be undone.
              </p>
              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => setDeleteConfirm(null)}
                  className="px-4 py-2 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-full hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteFeedback}
                  disabled={deleteFeedback.isPending}
                  className="px-4 py-2 bg-red-600 text-white rounded-full hover:bg-red-700 disabled:opacity-50 transition-colors"
                >
                  {deleteFeedback.isPending ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  if (view === 'assigned') {
    return (
      <div className="p-6 space-y-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Assigned Feedback</h1>

        <div className="grid gap-4">
          {assignedInstances.map((instance) => {
            const feedback = feedbackList.find((s) => s.id === instance.feedbackId);
            const hasResponded = instance.hasUserResponded ?? false;

            return (
              <div
                key={instance.id}
                className={`bg-white dark:bg-gray-800 p-6 rounded-lg shadow-sm border transition-all ${
                  hasResponded
                    ? 'border-green-200 dark:border-green-800 bg-green-50/50 dark:bg-green-900/10'
                    : 'border-gray-200 dark:border-gray-700'
                }`}
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                          {feedback?.title || 'Loading...'}
                        </h3>
                        {instance.assigneeType === 'user' ? (
                          <span className="px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-400 rounded-full text-xs font-medium flex items-center">
                            <User className="h-3 w-3 mr-1" />
                            Direct
                          </span>
                        ) : (
                          <span className="px-2 py-1 bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-400 rounded-full text-xs font-medium flex items-center">
                            <Users className="h-3 w-3 mr-1" />
                            Team
                          </span>
                        )}
                      </div>
                      {hasResponded && (
                        <span className="ml-3 px-3 py-1 bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400 rounded-full text-xs font-medium flex items-center">
                          <CheckCircle className="h-3 w-3 mr-1" />
                          Completed
                        </span>
                      )}
                    </div>
                    <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                      {feedback?.description || ''}
                    </p>
                    <div className="mt-4 flex items-center space-x-4 text-sm text-gray-500 dark:text-gray-400">
                      <span className="flex items-center">
                        <Clock className="h-4 w-4 mr-1" />
                        {instance.frequency}
                      </span>
                    </div>
                  </div>
                  {!hasResponded && feedback && (
                    <button
                      onClick={() => navigate(`/feedback/take/${feedback.id}/${instance.feedbackInstanceId}?occurrenceId=${instance.id}`)}
                      className="ml-4 px-4 py-2 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors text-sm"
                    >
                      Take Feedback
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
          Welcome back, {currentUser?.name}!
        </h1>
        <span className="px-3 py-1 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 rounded-full text-sm capitalize">
          {currentUser?.role}
        </span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat) => (
          <div
            key={stat.title}
            className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">{stat.title}</p>
                <p className="mt-2 text-3xl font-semibold text-gray-900 dark:text-white">
                  {stat.value}
                </p>
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-500">{stat.description}</p>
              </div>
              <div className={`${stat.color} p-3 rounded-full`}>
                <stat.icon className="h-6 w-6 text-white" />
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Recent Feedback</h2>
            <button
              onClick={() => navigate('/feedback/list')}
              className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
            >
              View all
            </button>
          </div>
          <div className="space-y-3">
            {userFeedback.slice(0, 3).map((feedback) => (
              <div
                key={feedback.id}
                className="flex justify-between items-center p-3 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-md cursor-pointer"
                onClick={() => navigate(`/feedback/edit/${feedback.id}`)}
              >
                <div className="flex-1">
                  <div className="flex items-center space-x-2">
                    <p className="font-medium text-gray-900 dark:text-white">{feedback.title}</p>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                      feedback.isPublished && feedback.publishedVersions && feedback.publishedVersions.length > 0
                        ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                        : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                    }`}>
                      {feedback.isPublished && feedback.publishedVersions && feedback.publishedVersions.length > 0
                        ? `v${feedback.publishedVersions.join(', v')}`
                        : 'Draft'}
                    </span>
                  </div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {feedback.questionCount} questions
                  </p>
                </div>
                <ArrowRight className="h-4 w-4 text-gray-400" />
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Quick Actions</h2>
          </div>
          <div className="space-y-3">
            {currentUser?.role !== 'respondent' && (
              <button
                onClick={() => navigate('/feedback/create')}
                className="w-full text-left p-3 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-md flex items-center justify-between"
              >
                <span className="text-gray-900 dark:text-white">Create New Feedback</span>
                <ArrowRight className="h-4 w-4 text-gray-400" />
              </button>
            )}
            <button
              onClick={() => navigate('/feedback/assigned')}
              className="w-full text-left p-3 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-md flex items-center justify-between"
            >
              <span className="text-gray-900 dark:text-white">View Assigned Feedback</span>
              <ArrowRight className="h-4 w-4 text-gray-400" />
            </button>
            {currentUser?.role !== 'respondent' && (
              <button
                onClick={() => navigate('/instances')}
                className="w-full text-left p-3 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-md flex items-center justify-between"
              >
                <span className="text-gray-900 dark:text-white">Manage Instances</span>
                <ArrowRight className="h-4 w-4 text-gray-400" />
              </button>
            )}
          </div>
        </div>
      </div>

      {deleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-red-100 dark:bg-red-900/30 rounded-full">
                <AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-400" />
              </div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                Delete Feedback
              </h2>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
              Are you sure you want to delete <span className="font-medium text-gray-900 dark:text-white">{deleteConfirm.title}</span>? This will permanently remove the feedback, all its versions, questions, and any associated responses. This action cannot be undone.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-full hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteFeedback}
                disabled={deleteFeedback.isPending}
                className="px-4 py-2 bg-red-600 text-white rounded-full hover:bg-red-700 disabled:opacity-50 transition-colors"
              >
                {deleteFeedback.isPending ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;